import api from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PrinterRole = 'RECEIPT' | 'KITCHEN' | 'LAUNDRY' | 'BAR';
export type PrintJobStatus = 'pending' | 'dispatched' | 'printed' | 'failed' | 'timeout' | 'retrying' | 'dead';
export type ContentType = 'kot' | 'receipt' | 'tax_invoice' | 'laundry';

export interface PrinterAgent {
  id: string;
  name: string;
  location: string | null;
  is_online: boolean;
  last_heartbeat_at: string | null;
  created_at: string;
  updated_at: string;
  printers: Printer[];
}

export interface Printer {
  id: string;
  agent_id: string;
  name: string;
  role: PrinterRole;
  is_active: boolean;
  paper_width_mm: number;
  created_at: string;
  updated_at: string;
  agent_name?: string;
  agent_is_online?: boolean;
}

export interface PrintJob {
  id: string;
  idempotency_key: string;
  printer_role: PrinterRole;
  status: PrintJobStatus;
  content_type: ContentType;
  metadata: Record<string, unknown>;
  assigned_agent_id: string | null;
  assigned_printer_id: string | null;
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  next_retry_at: string | null;
  printed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PrintStats {
  total: number;
  pending: number;
  dispatched: number;
  printed: number;
  failed: number;
  dead: number;
  agents: PrinterAgent[];
}

// ---------------------------------------------------------------------------
// Agent Management
// ---------------------------------------------------------------------------

export const agentService = {
  async list(): Promise<PrinterAgent[]> {
    const res = await api.get<PrinterAgent[]>('/printers/agents');
    return res.data!;
  },

  async get(id: string): Promise<PrinterAgent> {
    const res = await api.get<PrinterAgent>(`/printers/agents/${id}`);
    return res.data!;
  },

  async create(data: { name: string; location?: string }): Promise<PrinterAgent & { token: string }> {
    const res = await api.post<{ id: string; token: string }>('/printers/agents', data);
    return res.data;
  },

  async update(id: string, data: { name?: string; location?: string }): Promise<PrinterAgent> {
    const res = await api.put<PrinterAgent>(`/printers/agents/${id}`, data);
    return res.data!;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/printers/agents/${id}`);
  },

  async regenerateToken(id: string): Promise<{ token: string }> {
    const res = await api.post<{ token: string }>(`/printers/agents/${id}/regenerate-token`);
    return res.data;
  },
};

// ---------------------------------------------------------------------------
// Printer Management
// ---------------------------------------------------------------------------

export const printerService = {
  async list(): Promise<Printer[]> {
    const res = await api.get<Printer[]>('/printers');
    return res.data!;
  },

  async create(data: { agent_id: string; name: string; role: PrinterRole; paper_width_mm?: number }): Promise<Printer> {
    const res = await api.post<Printer>('/printers', data);
    return res.data;
  },

  async update(id: string, data: { name?: string; role?: PrinterRole; is_active?: boolean; paper_width_mm?: number }): Promise<Printer> {
    const res = await api.put<Printer>(`/printers/${id}`, data);
    return res.data!;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/printers/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Print Job Submission
// ---------------------------------------------------------------------------

export const printJobService = {
  async submit(data: {
    idempotency_key: string;
    printer_role: PrinterRole;
    content_type: ContentType;
    order_id: string;
    customer_details?: {
      customerName: string;
      companyName?: string;
      phone?: string;
      address?: string;
      tinVatNumber?: string;
    };
  }): Promise<{ jobId: string; status: string }> {
    const res = await api.post('/printers/print', data);
    return res.data;
  },

  async getStatus(jobId: string): Promise<PrintJob> {
    const res = await api.get<PrintJob>(`/printers/print/${jobId}`);
    return res.data!;
  },

  async retry(jobId: string): Promise<PrintJob> {
    const res = await api.post(`/printers/print/${jobId}/retry`);
    return res.data;
  },

  async getHistory(filters?: {
    status?: PrintJobStatus;
    printer_role?: PrinterRole;
    content_type?: ContentType;
    limit?: number;
    offset?: number;
  }): Promise<{ jobs: PrintJob[]; total: number }> {
    const params: Record<string, string | number> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.printer_role) params.printer_role = filters.printer_role;
    if (filters?.content_type) params.content_type = filters.content_type;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;
    const res = await api.get<PrintJob[]>('/printers/print/history', { params });
    return { jobs: res.data!, total: (res as any).total ?? 0 };
  },

  async getStats(): Promise<PrintStats> {
    const res = await api.get<PrintStats>('/printers/stats');
    return res.data!;
  },
};
